import * as React from 'react';
import { THUMBNAIL_COLUMNS } from './thumbnail-size-slider';

const STORAGE_KEY_PREFIX = 'thumbnail_columns:';

function isValidColumns(value: number) {
  return Number.isInteger(value) && value >= THUMBNAIL_COLUMNS.min && value <= THUMBNAIL_COLUMNS.max;
}

function readStoredColumns(storageKey: string): number | null {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return null;
    const parsed = Number(raw);
    return isValidColumns(parsed) ? parsed : null;
  } catch {
    // localStorage unavailable (private mode, disabled by policy): fall back to the default.
    return null;
  }
}

/**
 * Images per row picked with `ThumbnailSizeSlider`, remembered in localStorage so the size survives reloads.
 * Each grid passes its own `id` (e.g. `collection`, `assets`), so they keep separate sizes.
 * Not user-scoped on purpose: it's a UI preference, not user data.
 *
 * @example
 * const [columns, setColumns] = useThumbnailColumns('collection');
 */
function useThumbnailColumns(
  id: string,
  defaultColumns: number = THUMBNAIL_COLUMNS.default,
): readonly [number, (columns: number) => void] {
  const storageKey = `${STORAGE_KEY_PREFIX}${id}`;

  const [columns, setColumnsState] = React.useState<number>(() => readStoredColumns(storageKey) ?? defaultColumns);

  const setColumns = React.useCallback(
    (next: number) => {
      setColumnsState(next);
      try {
        window.localStorage.setItem(storageKey, String(next));
      } catch {
        // Storage write failed (quota, private mode); keep the in-memory value.
      }
    },
    [storageKey],
  );

  return [columns, setColumns] as const;
}

export { useThumbnailColumns };
